import { Capacitor } from '@capacitor/core'
import { LocalNotifications, type LocalNotificationSchema } from '@capacitor/local-notifications'
import type { ProgramContent } from '../types/content'

export interface NotificationSettings {
  start: boolean
  sevenDays: boolean
  oneDay: boolean
}

export const notificationStorageKey = 'gg-transport-notifications'

const notificationIds = {
  start: 42001,
  sevenDays: 42007,
  oneDay: 42011,
} as const

interface PlannedNotification {
  id: number
  title: string
  body: string
  at: Date
  kind: keyof NotificationSettings
}

function parseProgramDate(value: string | null) {
  if (!value) return null
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]), 9)
    : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function daysBefore(date: Date, days: number) {
  const result = new Date(date)
  result.setDate(result.getDate() - days)
  return result
}

export function createNotificationPlan(
  application: ProgramContent['application'],
  settings: NotificationSettings,
  now = new Date(),
): PlannedNotification[] {
  const startsAt = parseProgramDate(application.startsAt)
  const endsAt = parseProgramDate(application.endsAt)
  const candidates: Array<PlannedNotification | null> = [
    settings.start && startsAt
      ? {
          id: notificationIds.start,
          title: '교통비 지원 신청이 시작됐어요',
          body: '공식 신청 페이지에서 대상 여부와 신청 일정을 확인해 주세요.',
          at: startsAt,
          kind: 'start',
        }
      : null,
    settings.sevenDays && endsAt
      ? {
          id: notificationIds.sevenDays,
          title: '교통비 지원 신청 마감 7일 전이에요',
          body: '교통카드와 지역화폐 등 준비 항목을 확인해 주세요.',
          at: daysBefore(endsAt, 7),
          kind: 'sevenDays',
        }
      : null,
    settings.oneDay && endsAt
      ? {
          id: notificationIds.oneDay,
          title: '교통비 지원 신청 마감이 하루 남았어요',
          body: '신청 완료 여부를 공식 사이트에서 마지막으로 확인해 주세요.',
          at: daysBefore(endsAt, 1),
          kind: 'oneDay',
        }
      : null,
  ]

  return candidates.filter((item): item is PlannedNotification => Boolean(item && item.at > now))
}

export function isNativeApp() {
  return Capacitor.isNativePlatform()
}

export async function requestNativeNotificationPermission() {
  if (!isNativeApp()) return true
  const current = await LocalNotifications.checkPermissions()
  if (current.display === 'granted') return true
  const requested = await LocalNotifications.requestPermissions()
  return requested.display === 'granted'
}

export async function syncNativeNotifications(
  application: ProgramContent['application'],
  settings: NotificationSettings,
) {
  if (!isNativeApp()) return { scheduled: 0 }

  await LocalNotifications.cancel({
    notifications: Object.values(notificationIds).map((id) => ({ id })),
  })

  const plan = createNotificationPlan(application, settings)
  if (plan.length === 0) return { scheduled: 0 }

  await LocalNotifications.createChannel({
    id: 'application-deadlines',
    name: '신청 일정 알림',
    description: '교통비 지원 신청 시작과 마감 일정을 알려드립니다.',
    importance: 3,
    visibility: 1,
    vibration: true,
  })

  const notifications: LocalNotificationSchema[] = plan.map((item) => ({
    id: item.id,
    title: item.title,
    body: item.body,
    channelId: 'application-deadlines',
    schedule: { at: item.at },
    isExactNotification: false,
    extra: { kind: item.kind },
  }))
  await LocalNotifications.schedule({ notifications })
  return { scheduled: notifications.length }
}

export async function syncNativeNotificationsIfPermitted(
  application: ProgramContent['application'],
  settings: NotificationSettings,
) {
  if (!isNativeApp()) return { scheduled: 0 }
  const permission = await LocalNotifications.checkPermissions()
  if (permission.display !== 'granted') return { scheduled: 0 }
  return syncNativeNotifications(application, settings)
}

export async function cancelNativeNotifications() {
  if (!isNativeApp()) return
  await LocalNotifications.cancel({
    notifications: Object.values(notificationIds).map((id) => ({ id })),
  })
}
